// PRODUCT-26110 Phase 2B2: 'vue-router/types' is a vue-router-3-only subpath, not
// present in v4's `exports` map at all -- module-not-found, not just a type mismatch.
// `Route` -> `RouteLocationNormalized`.
import type { App } from 'vue';

import type { RouteLocationNormalized } from 'vue-router';

import Logger from '@evidentid/logger';
import buildLoggerEventConsumers from '@evidentid/logger/buildLoggerEventConsumers';
import Tracker from '@evidentid/tracker';
import buildTrackerEventConsumers from '@evidentid/tracker/buildTrackerEventConsumers';
import slugify from '@evidentid/universal-framework/slugify';
import handleLegacyMithrilLocation from '@evidentid/vue-commons/compatibility/handleLegacyMithrilLocation';
import { Application as GenericApplication, ApplicationOptions } from '@evidentid/vue-commons/core/Application';
import getQueryValue from '@evidentid/vue-commons/router/getQueryValue';

import AppView from '@/components/App.vue';
import { createModalPlugin } from '@/components/common/Modal';
import support from '@/config/support';
import createVueRouter from '@/router';
import { attachSentryToVueApp } from '@/sentry';
import ErrorObject from '@/store/interfaces/ErrorObject';
import { useIdoStore } from '@/store/store';
import IdoLoggerUserData from '@/tracking/IdoLoggerUserData';
import IdoTrackerUserData from '@/tracking/IdoTrackerUserData';
import loggerEventConsumers from '@/tracking/loggerEventConsumers';
import { createIdoStoreObserver } from '@/tracking/storeObserver';
import trackerEventConsumers from '@/tracking/trackerEventConsumers';
import trackIdoEvents from '@/tracking/trackIdoEvents';
import getSessionData from '@/utils/getSessionData';

type IdoAppRouter = ReturnType<typeof createVueRouter>;
type IdoAppLogger = Logger<IdoLoggerUserData>;
type IdoAppTracker = Tracker<IdoTrackerUserData>;

class IdoFacingApplication extends GenericApplication<IdoAppLogger, IdoAppTracker, IdoAppRouter> {
    protected Component = AppView;

    public constructor(
        options: Omit<
            ApplicationOptions<IdoAppLogger, IdoAppTracker, IdoAppRouter>,
            'createRouter'
        >,
    ) {
        super({
            ...options,
            createRouter: createVueRouter,
        });
    }

    protected override beforeInitialize(): void {
        // Redirect legacy paths to new syntax
        handleLegacyMithrilLocation(this.options.location);
    }

    protected override afterInitialize(): void {
        // Bridge the Pinia store into the legacy observer contract trackIdoEvents
        // consumes (onValue/onMutation/onAction map to watch()/$onAction).
        const storeObserver = createIdoStoreObserver(useIdoStore(this.pinia), this.observer);

        // Consume events in logger
        trackIdoEvents(storeObserver, buildLoggerEventConsumers(this.logger, loggerEventConsumers));

        // Consume events in tracker
        trackIdoEvents(storeObserver, buildTrackerEventConsumers(this.tracker, trackerEventConsumers));
    }

    // PRODUCT-26110 Task 14: Modal plugin registration moved out of the legacy
    // `(this.options.Vue as any).use(...)` global-singleton path (only worked via
    // @vue/compat) and into this real Vue 3 `app.use()` call, run by the base class's
    // mount() against the actual createApp() instance -- see
    // vue-commons/core/Application.ts's `installAppPlugins` extension point.
    protected override installAppPlugins(app: App): void {
        app.use(createModalPlugin());
        // Attach @sentry/vue's error hooking to the real app instance; chains the
        // errorHandler the base class registered just before this hook runs.
        attachSentryToVueApp(app);
    }

    protected override onVueError(error: Error): void {
        useIdoStore(this.pinia).showError(error as unknown as ErrorObject);
    }

    protected override onRouteChange(route: RouteLocationNormalized): void {
        // Clean up full-screen errors after routing change.
        // It should be dispatched anyway, but it doesn't look clean in Vuex log.
        const store = useIdoStore(this.pinia);
        if (route.name !== 'error' && store.error) {
            store.showError(null);
        }
    }

    protected override beforeMount(): void {
        if (!this.router) {
            throw new Error('Router is required to resolve application.');
        }

        const keys = [ 'q', 'rprId', 'canary', 'continue_url', 'enable_return', 'case_id', 'auth_code' ];
        const [ question, rprId, canary, continueUrl, enableReturn, caseId, authCode ]
            = getQueryValue(this.router, this.options.location, ...keys);

        // Redirect to question, when it was passed in query parameters
        if (question) {
            this.router.push({ name: 'question', params: { slug: slugify(question) } });
        }

        // Inform logger about support
        const storage = support.sessionStorage ? window.sessionStorage : null;
        this.logger?.setTag('session-storage', storage ? 'yes' : 'no');

        // Inform user about possible problems with session storage
        if (!storage) {
            useIdoStore(this.pinia).displaySnackbar({
                permanent: true,
                message: 'Since you’re using your browser’s incognito mode, please be aware that refreshing the page will result in losing your progress and will require starting over.',
            });
        }

        // Initialize current session
        const parsedEnableReturn = enableReturn === 'true';
        const parsedCaseId = caseId ? parseInt(caseId, 10) : null;
        const sessionData = getSessionData(
            storage, rprId, canary, continueUrl, parsedEnableReturn, parsedCaseId, authCode,
        );
        useIdoStore(this.pinia).setSessionData(sessionData);
    }
}

export default IdoFacingApplication;
